import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, AlertTriangle, ChevronRight, ArrowLeft, Loader2, Play, MessageSquare } from "lucide-react";
import { PostSessionFeedback } from "@/components/PostSessionFeedback";
import { PERSONA_AVATARS } from "@/lib/persona-avatars";
import { motion, AnimatePresence } from "framer-motion";

const STAGE_NAMES = {
  ankommen: "Ankommen", verstehen: "Verstehen", aufarbeiten: "Aufarbeiten",
  trainieren: "Trainieren", vertiefen: "Vertiefen", simulation: "Prüfungssimulation"
};

const STAGE_MESSAGES = {
  ankommen: "Gut gemacht. Sie haben den ersten Schritt geschafft.",
  verstehen: "Sie erkennen jetzt, worauf es ankommt.",
  aufarbeiten: "Ihre Antworten werden konkreter. Weiter so.",
  trainieren: "Sie werden sicherer im Gespräch.",
  vertiefen: "Sie bleiben auch unter Druck stabil.",
  simulation: "Prüfungssimulation abgeschlossen. Starke Leistung.",
};

function StageCompletionBanner({ stage, onDismiss }) {
  const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const timer = setTimeout(onDismiss, 4500);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const stageName = STAGE_NAMES[stage] || stage;
  const message = STAGE_MESSAGES[stage] || "Stufe abgeschlossen.";

  return (
    <motion.div
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="rounded-xl border border-primary/20 bg-primary/5 p-5 flex items-center gap-4"
      data-testid="stage-completion-banner"
    >
      <motion.div
        initial={prefersReducedMotion ? {} : { scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 15, delay: 0.2 }}
        className="w-11 h-11 rounded-full bg-primary flex items-center justify-center flex-shrink-0"
      >
        <Check className="w-6 h-6 text-white" strokeWidth={3} />
      </motion.div>
      <div>
        <motion.p
          initial={prefersReducedMotion ? {} : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="font-heading text-base font-bold text-primary"
        >
          Stufe „{stageName}" abgeschlossen
        </motion.p>
        <motion.p
          initial={prefersReducedMotion ? {} : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="font-body text-sm text-muted-foreground mt-0.5"
        >
          {message}
        </motion.p>
      </div>
    </motion.div>
  );
}

export default function SessionAnalysisPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConversation, setShowConversation] = useState(false);
  const [showCompletionBanner, setShowCompletionBanner] = useState(false);

  // Learning-specific data from navigation state
  const learningData = location.state?.learning;

  // Show stage completion banner on first load if stage was just completed
  useEffect(() => {
    if (learningData?.stage_completed) {
      setShowCompletionBanner(true);
    }
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/training/sessions/${sessionId}`);
        setSession(res.data);
      } catch { navigate("/lernpfad"); }
      setLoading(false);
    };
    fetch();
  }, [sessionId, navigate]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!session) return null;

  const a = session.analysis || {};
  const stage = session.learning_stage || learningData?.stage || "";
  const stageName = STAGE_NAMES[stage] || "";

  // Use learning-specific data if available, otherwise fall back to session analysis
  const goodPoints = learningData?.what_went_well || a.what_went_well || a.strengths || [];
  const workPoints = learningData?.what_needs_work || a.what_needs_work || a.weaknesses || [];
  const keyQuotes = learningData?.key_quotes || a.key_quotes || [];
  const nextStep = learningData?.next_step || a.next_step || a.improvement_suggestions?.[0] || "";
  const progressNote = learningData?.progress_note || a.progress_note || a.detailed_feedback || "";
  const nextStage = learningData?.next_stage || "";

  return (
    <div className="page-transition max-w-2xl mx-auto space-y-6" data-testid="analysis-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/lernpfad")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Nachbesprechung</h1>
          {stageName && <Badge variant="secondary" className="font-body text-xs mt-1">{stageName}</Badge>}
        </div>
      </div>

      {/* Stage Completion Animation */}
      <AnimatePresence>
        {showCompletionBanner && stage && (
          <StageCompletionBanner stage={stage} onDismiss={() => setShowCompletionBanner(false)} />
        )}
      </AnimatePresence>

      {/* Progress Note (how the session went overall) */}
      {progressNote && (
        <Card className="border-border/50">
          <CardContent className="pt-6 pb-4">
            <p className="font-body text-sm leading-relaxed">{progressNote}</p>
          </CardContent>
        </Card>
      )}

      {/* Block A: Was gut lief */}
      <Card className="border-l-4 border-l-success" data-testid="good-points">
        <CardContent className="pt-6 pb-4">
          <div className="flex items-center gap-2 mb-3">
            <Check className="w-5 h-5 text-success" />
            <p className="font-body text-sm font-semibold">Was gut lief</p>
          </div>
          {goodPoints.length > 0 ? (
            <ul className="space-y-2">
              {goodPoints.slice(0, 3).map((point, i) => (
                <li key={i} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 flex-shrink-0" />
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">{point}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="font-body text-sm text-muted-foreground">Wird nach der nächsten Session ausgewertet.</p>
          )}
        </CardContent>
      </Card>

      {/* Block B: Was noch Arbeit braucht */}
      <Card className="border-l-4 border-l-warning" data-testid="work-points">
        <CardContent className="pt-6 pb-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-warning" />
            <p className="font-body text-sm font-semibold">Was noch Arbeit braucht</p>
          </div>
          {workPoints.length > 0 ? (
            <ul className="space-y-2">
              {workPoints.slice(0, 3).map((point, i) => (
                <li key={i} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-warning mt-2 flex-shrink-0" />
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">{point}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="font-body text-sm text-muted-foreground">Keine kritischen Punkte in dieser Session.</p>
          )}
        </CardContent>
      </Card>

      {/* Block C: Schlüssel-Zitate */}
      {keyQuotes.length > 0 && (
        <Card className="border-border/50" data-testid="key-quotes">
          <CardContent className="pt-6 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-5 h-5 text-primary" />
              <p className="font-body text-sm font-semibold">Schlüssel-Zitate</p>
            </div>
            <div className="space-y-3">
              {keyQuotes.slice(0, 4).map((q, i) => (
                <div key={i} className="flex items-start gap-2 pl-1">
                  <Badge variant="outline" className="font-body text-[9px] flex-shrink-0 mt-0.5">
                    {q.category === "einsicht" ? "Einsicht" : q.category === "risiko" ? "Risiko" : q.category === "fortschritt" ? "Fortschritt" : q.category === "emotion" ? "Emotion" : q.category}
                  </Badge>
                  <div>
                    <p className="font-body text-sm italic text-foreground leading-relaxed">"{q.text}"</p>
                    {q.context && <p className="font-body text-xs text-muted-foreground mt-0.5">{q.context}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Block D: Nächster Schritt */}
      <Card className="border-primary/20 bg-primary/5" data-testid="next-step">
        <CardContent className="pt-6 pb-4">
          <p className="font-body text-xs font-medium text-primary mb-2">Ihr nächster Schritt</p>
          <p className="font-body text-sm leading-relaxed">{nextStep || "Machen Sie mit der nächsten empfohlenen Stufe weiter."}</p>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button className="flex-1 font-body bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
          onClick={() => navigate("/lernpfad")} data-testid="back-to-path">
          <Play className="w-4 h-4" /> Zurück zum Lernpfad
        </Button>
        {nextStage && nextStage !== stage && (
          <Button variant="outline" className="flex-1 font-body gap-2"
            onClick={async () => {
              try {
                const res = await api.post(`/learning/session?stage=${nextStage}`);
                navigate(`/conversation/${res.data.session_id}`);
              } catch { navigate("/lernpfad"); }
            }} data-testid="next-stage-btn">
            Nächste Stufe: {STAGE_NAMES[nextStage] || nextStage} <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Post-Session Feedback */}
      <PostSessionFeedback sessionId={sessionId} />

      {/* Conversation (expandable) */}
      <details className="mt-2">
        <summary className="font-body text-xs text-muted-foreground cursor-pointer text-center">Gesprächsverlauf anzeigen</summary>
        <div className="mt-3 space-y-2 max-h-[400px] overflow-y-auto">
          {(session.messages || []).map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-xl ${msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                <p className="font-body text-xs leading-relaxed">{msg.content}</p>
              </div>
            </div>
          ))}
        </div>
      </details>

      {/* Disclaimer */}
      <p className="font-body text-xs text-muted-foreground text-center">
        Diese Auswertung dient als Trainings-Feedback. Sie ersetzt keine professionelle MPU-Beratung.
      </p>
    </div>
  );
}
